import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { FiGrid, FiNavigation, FiPlus, FiEdit2, FiTrash2, FiStar, FiSettings, FiCalendar, FiDroplet } from 'react-icons/fi';
import { establishmentsAPI, establishmentAddressesAPI } from '../../services/api';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const EstablishmentSettings = () => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const [loading, setLoading] = useState(false);

  // Establishment info state
  const [establishmentInfo, setEstablishmentInfo] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    country: '',
    postal_code: '',
    phone: '',
    email: '',
    website: ''
  });

  // Behavior settings state
  const [autoPostponeOrders, setAutoPostponeOrders] = useState(true);
  const [showScheduleColors, setShowScheduleColors] = useState(false);
  const [isSavingBehavior, setIsSavingBehavior] = useState(false);

  // Addresses state
  const [addresses, setAddresses] = useState([]);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [deleteAddressConfirm, setDeleteAddressConfirm] = useState(null);

  useEffect(() => {
    if (user?.establishment_id) {
      fetchEstablishmentInfo();
      fetchAddresses();
    }
  }, [user]);

  const fetchEstablishmentInfo = async () => {
    try {
      const data = await establishmentsAPI.getById(user.establishment_id);
      setEstablishmentInfo(data);
      setFormData({
        name: data.name || '',
        address: data.address || '',
        city: data.city || '',
        country: data.country || '',
        postal_code: data.postal_code || '',
        phone: data.phone || '',
        email: data.email || '',
        website: data.website || ''
      });
      // Load behavior settings
      setAutoPostponeOrders(data.auto_postpone_orders ?? true);
      setShowScheduleColors(data.show_schedule_colors ?? false);
    } catch (error) {
      showNotification('Error al cargar información del establecimiento', 'error');
    }
  };

  const fetchAddresses = async () => {
    try {
      setIsLoadingAddresses(true);
      const data = await establishmentAddressesAPI.getAll({ establishment_id: user.establishment_id });
      setAddresses(data);
    } catch (error) {
      console.error('Error loading addresses:', error);
    } finally {
      setIsLoadingAddresses(false);
    }
  };

  const handleCreateAddress = () => {
    setSelectedAddress(null);
    setShowAddressForm(true);
  };

  const handleEditAddress = (address) => {
    setSelectedAddress(address);
    setShowAddressForm(true);
  };

  const handleDeleteAddress = (address) => {
    setDeleteAddressConfirm(address);
  };

  const confirmDeleteAddress = async () => {
    try {
      await establishmentAddressesAPI.delete(deleteAddressConfirm.id);
      showNotification('Dirección eliminada exitosamente', 'success');
      setDeleteAddressConfirm(null);
      fetchAddresses();
    } catch (error) {
      showNotification('Error al eliminar la dirección', 'error');
    }
  };

  const handleSetPrimaryAddress = async (address) => {
    try {
      await establishmentAddressesAPI.setPrimary(address.id);
      showNotification('Dirección marcada como principal', 'success');
      fetchAddresses();
    } catch (error) {
      showNotification('Error al marcar como principal', 'error');
    }
  };

  const handleSaveAddress = async (addressData) => {
    try {
      if (selectedAddress) {
        await establishmentAddressesAPI.update(selectedAddress.id, addressData);
        showNotification('Dirección actualizada exitosamente', 'success');
      } else {
        await establishmentAddressesAPI.create({
          ...addressData,
          establishment_id: user.establishment_id
        });
        showNotification('Dirección creada exitosamente', 'success');
      }
      setShowAddressForm(false);
      setSelectedAddress(null);
      fetchAddresses();
    } catch (error) {
      showNotification('Error al guardar la dirección', 'error');
    }
  };

  const handleUpdateEstablishment = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      await establishmentsAPI.update(user.establishment_id, formData);
      showNotification('Información actualizada exitosamente', 'success');
      setEditMode(false);
      fetchEstablishmentInfo();
    } catch (error) {
      showNotification(
        error.response?.data?.detail || 'Error al actualizar la información',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type) => {
    addNotification({ message, type });
  };

  const handleToggleAutoPostpone = async () => {
    try {
      setIsSavingBehavior(true);
      const newValue = !autoPostponeOrders;
      await establishmentsAPI.update(user.establishment_id, {
        auto_postpone_orders: newValue
      });
      setAutoPostponeOrders(newValue);
      showNotification(
        newValue
          ? 'Las órdenes incompletas se pasarán automáticamente al día siguiente'
          : 'Las órdenes incompletas ya no se pasarán automáticamente',
        'success'
      );
    } catch (error) {
      showNotification('Error al actualizar la configuración', 'error');
    } finally {
      setIsSavingBehavior(false);
    }
  };

  const handleToggleScheduleColors = async () => {
    try {
      setIsSavingBehavior(true);
      const newValue = !showScheduleColors;
      await establishmentsAPI.update(user.establishment_id, {
        show_schedule_colors: newValue
      });
      setShowScheduleColors(newValue);
      showNotification(
        newValue
          ? 'Los colores se mostrarán en los cuadrantes'
          : 'Los colores ya no se mostrarán en los cuadrantes',
        'success'
      );
    } catch (error) {
      showNotification('Error al actualizar la configuración', 'error');
    } finally {
      setIsSavingBehavior(false);
    }
  };

  const renderBehaviorSection = () => {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mt-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-6">
          <FiSettings />
          Configuración de Comportamiento
        </h3>

        <div className="space-y-6">
          {/* Auto-postpone orders toggle */}
          <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex-1 pr-4">
              <div className="flex items-center gap-2 mb-1">
                <FiCalendar className="text-blue-600" />
                <span className="font-medium text-gray-900">
                  Posponer órdenes incompletas automáticamente
                </span>
              </div>
              <p className="text-sm text-gray-600">
                Cuando está activado, las órdenes de limpieza que no se completen durante el día
                se pasarán automáticamente al día siguiente. Si está desactivado, las órdenes
                incompletas permanecerán en su fecha original.
              </p>
            </div>
            <div className="flex items-center">
              <button
                onClick={handleToggleAutoPostpone}
                disabled={isSavingBehavior}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  autoPostponeOrders ? 'bg-blue-600' : 'bg-gray-200'
                } ${isSavingBehavior ? 'opacity-50 cursor-not-allowed' : ''}`}
                role="switch"
                aria-checked={autoPostponeOrders}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    autoPostponeOrders ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Show schedule colors toggle */}
          <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex-1 pr-4">
              <div className="flex items-center gap-2 mb-1">
                <FiDroplet className="text-purple-600" />
                <span className="font-medium text-gray-900">
                  Mostrar colores en cuadrantes
                </span>
              </div>
              <p className="text-sm text-gray-600">
                Cuando está activado, se mostrará una vista adicional de colores en los cuadrantes
                basada en los grupos de habitaciones asignados a cada empleado. Las horas siempre
                estarán disponibles independientemente de esta opción.
              </p>
            </div>
            <div className="flex items-center">
              <button
                onClick={handleToggleScheduleColors}
                disabled={isSavingBehavior}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                  showScheduleColors ? 'bg-purple-600' : 'bg-gray-200'
                } ${isSavingBehavior ? 'opacity-50 cursor-not-allowed' : ''}`}
                role="switch"
                aria-checked={showScheduleColors}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    showScheduleColors ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAddressesSection = () => {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mt-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FiNavigation />
            Direcciones / Ubicaciones
          </h3>
          <button
            onClick={handleCreateAddress}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <FiPlus className="h-4 w-4" />
            Nueva Dirección
          </button>
        </div>

        {isLoadingAddresses ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : addresses.length > 0 ? (
          <div className="space-y-4">
            {addresses.map((address) => (
              <div
                key={address.id}
                className={`border rounded-lg p-4 ${
                  address.is_primary ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="font-medium text-gray-900">{address.name}</h4>
                      {address.is_primary && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          <FiStar className="h-3 w-3 mr-1" />
                          Principal
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{address.address}</p>
                    {(address.city || address.state || address.postal_code) && (
                      <p className="text-sm text-gray-500">
                        {[address.city, address.state, address.postal_code].filter(Boolean).join(', ')}
                      </p>
                    )}
                    {address.country && (
                      <p className="text-sm text-gray-500">{address.country}</p>
                    )}
                    {(address.latitude && address.longitude) && (
                      <p className="text-xs text-gray-400 mt-1">
                        Coordenadas: {address.latitude.toFixed(6)}, {address.longitude.toFixed(6)}
                      </p>
                    )}
                    {address.notes && (
                      <p className="text-sm text-gray-500 mt-2 italic">{address.notes}</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    {!address.is_primary && (
                      <button
                        onClick={() => handleSetPrimaryAddress(address)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Marcar como principal"
                      >
                        <FiStar className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleEditAddress(address)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Editar"
                    >
                      <FiEdit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteAddress(address)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Eliminar"
                    >
                      <FiTrash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <FiNavigation className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No hay direcciones registradas</p>
            <p className="text-sm text-gray-400 mt-1">
              Añade direcciones para localizar las ubicaciones del establecimiento
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderInfoSection = () => {
    if (!establishmentInfo) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FiGrid />
            Información del Establecimiento
          </h3>
          {!editMode && (
            <button
              onClick={() => setEditMode(true)}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Editar
            </button>
          )}
        </div>

        {editMode ? (
          <form onSubmit={handleUpdateEstablishment} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Teléfono
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sitio Web
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dirección
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ciudad
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  País
                </label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Código Postal
                </label>
                <input
                  type="text"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setEditMode(false);
                  fetchEstablishmentInfo();
                }}
                className="px-6 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Nombre</p>
                <p className="font-semibold">{establishmentInfo.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Teléfono</p>
                <p className="font-semibold">{establishmentInfo.phone || 'No especificado'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-semibold">{establishmentInfo.email || 'No especificado'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Sitio Web</p>
                <p className="font-semibold">{establishmentInfo.website || 'No especificado'}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm text-gray-600">Dirección</p>
                <p className="font-semibold">{establishmentInfo.address || 'No especificada'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Ciudad</p>
                <p className="font-semibold">{establishmentInfo.city || 'No especificada'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">País</p>
                <p className="font-semibold">{establishmentInfo.country || 'No especificado'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Código Postal</p>
                <p className="font-semibold">{establishmentInfo.postal_code || 'No especificado'}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">Gestión del Establecimiento</h1>

        {/* Establishment Info Section */}
        {renderInfoSection()}

        {/* Behavior Settings Section */}
        {renderBehaviorSection()}

        {/* Addresses Section */}
        {renderAddressesSection()}
      </div>

      {/* Address Form Modal */}
      {showAddressForm && (
        <AddressFormDialog
          address={selectedAddress}
          onSave={handleSaveAddress}
          onClose={() => {
            setShowAddressForm(false);
            setSelectedAddress(null);
          }}
        />
      )}

      {/* Delete Address Confirmation */}
      {deleteAddressConfirm && (
        <ConfirmDialog
          title="Eliminar Dirección"
          message={`¿Estás seguro de que deseas eliminar la dirección "${deleteAddressConfirm.name}"? Esta acción no se puede deshacer.`}
          confirmText="Eliminar"
          confirmStyle="danger"
          onConfirm={confirmDeleteAddress}
          onCancel={() => setDeleteAddressConfirm(null)}
        />
      )}
    </div>
  );
};

// Address Form Dialog Component
const AddressFormDialog = ({ address, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: address?.name || '',
    address: address?.address || '',
    city: address?.city || '',
    state: address?.state || '',
    postal_code: address?.postal_code || '',
    country: address?.country || '',
    latitude: address?.latitude || '',
    longitude: address?.longitude || '',
    notes: address?.notes || '',
    is_primary: address?.is_primary || false
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    const dataToSave = {
      ...formData,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null
    };

    await onSave(dataToSave);
    setIsSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {address ? 'Editar Dirección' : 'Nueva Dirección'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Ej: Sede Principal, Almacén, etc."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dirección *
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                placeholder="Calle, número, piso..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ciudad
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Provincia/Estado
                </label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Código Postal
                </label>
                <input
                  type="text"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  País
                </label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Coordenadas (opcional)
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Latitud
                  </label>
                  <input
                    type="number"
                    name="latitude"
                    value={formData.latitude}
                    onChange={handleChange}
                    step="any"
                    placeholder="Ej: 40.416775"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Longitud
                  </label>
                  <input
                    type="number"
                    name="longitude"
                    value={formData.longitude}
                    onChange={handleChange}
                    step="any"
                    placeholder="Ej: -3.703790"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notas
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                placeholder="Información adicional sobre esta ubicación..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_primary"
                name="is_primary"
                checked={formData.is_primary}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="is_primary" className="ml-2 text-sm text-gray-700">
                Marcar como dirección principal
              </label>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {isSaving ? 'Guardando...' : address ? 'Guardar Cambios' : 'Crear Dirección'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EstablishmentSettings;
